/* eslint-disable max-lines */
import { useMutation } from '@tanstack/react-query';
import { type ColumnDef, createColumnHelper } from '@tanstack/table-core';
import { DatabaseIcon, UserIcon } from 'lucide-react';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { TableHeader } from '@/components/DataTable/TableHeader';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { Toaster } from '@/hooks/use-toast';
import type { ProjectIntegrations } from '@/lib/queries/admin';
import type { UnconnectedK8SCluster } from '@/lib/queries/integrations/containers/k8sClusters';
import { listSlackChannels } from '@/lib/queries/integrations/messaging/slack';

const columnsHelper = createColumnHelper<ProjectIntegrations>();

const OK = '✅ Onboarded';
const WARNING = '⚠️ Onboarded with issues';
const ERROR = '🚨 Onboarding failed';
const NOT_ONBOARDED = '❌ Not onboarded';
const PENDING = '⏳ Onboarding in progress';

const simpleValidator = (v: { status: string }) => v.status === 'active';
const simpleErrorValidator = (v: { status: string }) => v.status === 'error';
const bucketValidator = (v: { accessStatus: string; syncStatus: string }) =>
  v.accessStatus === 'active' && v.syncStatus === 'synced';
const bucketErrorValidator = (v: { accessStatus: string }) =>
  v.accessStatus === 'error';
const githubValidator = () => true;
const githubErrorValidator = () => false;
const grafanaValidator = (v: { status: string }) => v.status === 'active';
const grafanaErrorValidator = (v: { status: string }) => v.status === 'error';
const gcpValidator = (v: { status: string }) => v.status === 'active';
const gcpErrorValidator = (v: { status: string }) => v.status === 'error';
const gitlabValidator = () => true; // GitLab configurations are considered valid if they exist
const gitlabErrorValidator = () => false; // No error state defined yet
const k8sValidator = (v: { last_heartbeat: string; init: boolean }) => {
  const heartbeatTime = new Date(v.last_heartbeat);
  const now = new Date();
  const timeDiffMinutes =
    (now.getTime() - heartbeatTime.getTime()) / (1000 * 60);

  return v.init && timeDiffMinutes <= 5;
};
const k8sErrorValidator = (v: { last_heartbeat: string; init: boolean }) => {
  if (!v.init) {
    return true;
  }
  const heartbeatTime = new Date(v.last_heartbeat);
  const now = new Date();
  const timeDiffMinutes =
    (now.getTime() - heartbeatTime.getTime()) / (1000 * 60);

  return timeDiffMinutes > 15;
};
const unconnectedK8sValidator = (v: UnconnectedK8SCluster) =>
  v.status === 'ACTIVE' && v.available_for_connection;

const SlackStatusCell = ({ projectId }: { projectId: string }) => {
  const [slackData, setSlackData] = useState<{
    channels: number;
    error?: string;
  } | null>(null);
  const [hasRequested, setHasRequested] = useState(false);

  const slackMutation = useMutation({
    mutationFn: async () => {
      const response = await listSlackChannels(projectId);

      return response;
    },
    onSuccess: data => {
      setSlackData({
        channels: data.channels.length,
      });
    },
    onError: () => {
      setSlackData({
        channels: 0,
        error: 'Error',
      });
    },
  });

  const handleFetchStatus = () => {
    setHasRequested(true);
    slackMutation.mutate();
  };

  if (!hasRequested) {
    return (
      <Button variant="outline" size="sm" onClick={handleFetchStatus}>
        <FormattedMessage id="admin.table.slack.fetchStatus" />
      </Button>
    );
  }

  if (slackMutation.isPending) {
    return (
      <div>
        ⏳ <FormattedMessage id="admin.table.slack.loading" />
      </div>
    );
  }

  if (slackData?.error !== undefined) {
    return (
      <div>
        🚨 <FormattedMessage id="admin.table.slack.error" />
      </div>
    );
  }

  if (slackData) {
    if (slackData.channels > 0) {
      return (
        <div>
          ✅{' '}
          <FormattedMessage
            id="admin.table.slack.ok"
            values={{ count: slackData.channels }}
          />
        </div>
      );
    } else {
      return (
        <div>
          ❌ <FormattedMessage id="admin.table.slack.notConnected" />
        </div>
      );
    }
  }

  return (
    <div>
      ❓ <FormattedMessage id="admin.table.slack.na" />
    </div>
  );
};

const getK8sCell = (
  connectedClusters: ProjectIntegrations['k8sClusters'],
  _unconnectedClusters: ProjectIntegrations['unconnected_k8sClusters'],
  value: string,
) => {
  const safeConnectedClusters = connectedClusters;

  const connectedOkCount = safeConnectedClusters.filter(k8sValidator).length;
  const connectedErrorCount =
    safeConnectedClusters.filter(k8sErrorValidator).length;
  const connectedTotalCount = safeConnectedClusters.length;

  return (
    <div>
      <p>{value}</p>
      {/* Connected clusters */}
      <p className="text-gray-500 dark:text-gray-400">
        {connectedOkCount}{' '}
        <FormattedMessage id="admin.table.k8sClusters.connected" />
      </p>
      <p className="text-gray-500 dark:text-gray-400">
        {connectedTotalCount - connectedOkCount - connectedErrorCount}{' '}
        <FormattedMessage id="admin.table.k8sClusters.pending" />
      </p>
      <p className="text-gray-500 dark:text-gray-400">
        {connectedErrorCount}{' '}
        <FormattedMessage id="admin.table.k8sClusters.error" />
      </p>
    </div>
  );
};

const getCell = <T,>(
  array: T[] | undefined,
  value: string,
  validator: (v: T) => boolean,
  errorValidator: (v: T) => boolean,
) => {
  const safeArray = array ?? [];
  const okCount = safeArray.filter(validator).length;
  const errorCount = safeArray.filter(v => errorValidator(v)).length;
  const totalCount = safeArray.length;

  return (
    <div>
      <p>{value}</p>
      <p className="text-gray-500 dark:text-gray-400">{okCount} OK</p>
      <p className="text-gray-500 dark:text-gray-400">
        {totalCount - okCount - errorCount} Pending
      </p>
      <p className="text-gray-500 dark:text-gray-400">{errorCount} Error</p>
    </div>
  );
};

const getEmoji = <T,>(
  array: T[] | undefined,
  validator: (v: T) => boolean,
  errorValidator: (v: T) => boolean,
) => {
  const safeArray = array ?? [];
  const okCount = safeArray.filter(validator).length;
  const errorCount = safeArray.filter(v => errorValidator(v)).length;
  const totalCount = safeArray.length;

  if (okCount > 0 && errorCount === 0) {
    return OK;
  } else if (okCount > 0) {
    return WARNING;
  } else if (errorCount > 0) {
    return ERROR;
  } else if (totalCount === 0) {
    return NOT_ONBOARDED;
  } else {
    return PENDING;
  }
};

const getK8sEmoji = (
  connectedClusters: ProjectIntegrations['k8sClusters'],
  unconnectedClusters: ProjectIntegrations['unconnected_k8sClusters'],
) => {
  const safeConnectedClusters = connectedClusters;
  const safeUnconnectedClusters = unconnectedClusters ?? [];

  const connectedOkCount = safeConnectedClusters.filter(k8sValidator).length;
  const unconnectedActiveCount = safeUnconnectedClusters.filter(
    unconnectedK8sValidator,
  ).length;
  const totalDiscoveredCount = connectedOkCount + unconnectedActiveCount;

  // Success: all discovered clusters are running the k8s agent
  if (totalDiscoveredCount > 0 && connectedOkCount === totalDiscoveredCount) {
    return OK;
  }
  // Partially onboarding: some discovered clusters are running the k8s agent
  else if (connectedOkCount > 0 && connectedOkCount < totalDiscoveredCount) {
    return `${WARNING} (${connectedOkCount}/${totalDiscoveredCount})`;
  }
  // Not onboarded: none of the discovered clusters are running the k8s agent
  else if (totalDiscoveredCount > 0) {
    return `${NOT_ONBOARDED} (${connectedOkCount}/${totalDiscoveredCount})`;
  } else {
    return NOT_ONBOARDED;
  }
};

export const getIntegrationsColumns = (
  impersonate: (id: string) => Promise<void>,
  defaultProjectId: string | undefined,
  toast: Toaster,
): ColumnDef<ProjectIntegrations>[] =>
  [
    columnsHelper.accessor('projectName', {
      header: ({ column }) => (
        <TableHeader
          column={column}
          headersTranslationPath="admin.table"
          type="input"
        />
      ),
      cell: ({ getValue, row }) => (
        <div className="flex flex-col">
          <div className="text-sm flex items-center">
            <p>{getValue()}</p>
            <TooltipProvider>
              <Tooltip delayDuration={100}>
                <TooltipTrigger asChild>
                  <div className="flex items-center border px-1 h-6 ml-2 rounded-sm gap-1">
                    <UserIcon className="h-4 w-4" />
                    <p>{row.original.members.length}</p>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <div>
                    {row.original.members.map(m => (
                      <p key={m.id}>{m.email}</p>
                    ))}
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <TooltipProvider>
              <Tooltip delayDuration={100}>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    className="p-0 h-6 w-6 ml-1 border rounded-sm"
                    onClick={() => {
                      navigator.clipboard.writeText(
                        row.original.neo4jDatabaseName,
                      );
                      toast({
                        title: 'global.copiedToClipboard',
                        duration: 800,
                      });
                    }}
                  >
                    <DatabaseIcon className="h-4 w-4 cursor-pointer" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  {row.original.neo4jDatabaseName}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <div className="text-[10px] text-gray-500 dark:text-gray-400">
            {row.original.members.find(m => m.role === 'admin')?.email}
          </div>
        </div>
      ),
    }),
    columnsHelper.accessor(
      c =>
        getEmoji(
          [...c.awsRoles, ...c.awsCredentials],
          simpleValidator,
          simpleErrorValidator,
        ),
      {
        id: 'awsCredentials',
        header: ({ column }) => (
          <TableHeader
            column={column}
            headersTranslationPath="admin.table"
            type="select"
          />
        ),
        cell: ({ getValue, row }) => {
          return getCell(
            [...row.original.awsRoles, ...row.original.awsCredentials],
            getValue(),
            simpleValidator,
            simpleErrorValidator,
          );
        },
      },
    ),
    columnsHelper.accessor(
      c =>
        getEmoji(c.githubInstallations, githubValidator, githubErrorValidator),
      {
        id: 'githubConfigurations',
        header: ({ column }) => (
          <TableHeader
            column={column}
            headersTranslationPath="admin.table"
            type="select"
          />
        ),
        cell: ({ getValue, row }) => {
          return getCell(
            row.original.githubInstallations,
            getValue(),
            githubValidator,
            githubErrorValidator,
          );
        },
      },
    ),
    columnsHelper.accessor(
      c =>
        getEmoji(c.gitlabConfigurations, gitlabValidator, gitlabErrorValidator),
      {
        id: 'gitlabConfigurations',
        header: ({ column }) => (
          <TableHeader
            column={column}
            headersTranslationPath="admin.table"
            type="select"
          />
        ),
        cell: ({ getValue, row }) => {
          return getCell(
            row.original.gitlabConfigurations,
            getValue(),
            gitlabValidator,
            gitlabErrorValidator,
          );
        },
      },
    ),
    columnsHelper.accessor(
      c => getEmoji(c.hcpCloudTokens, simpleValidator, simpleErrorValidator),
      {
        id: 'hcpCloudTokens',
        header: ({ column }) => (
          <TableHeader
            column={column}
            headersTranslationPath="admin.table"
            type="select"
          />
        ),
        cell: ({ getValue, row }) => {
          return getCell(
            row.original.hcpCloudTokens,
            getValue(),
            simpleValidator,
            simpleErrorValidator,
          );
        },
      },
    ),
    columnsHelper.accessor(
      c =>
        getEmoji(
          c.datadogApiCredentials,
          simpleValidator,
          simpleErrorValidator,
        ),
      {
        id: 'datadogApiCredentials',
        header: ({ column }) => (
          <TableHeader
            column={column}
            headersTranslationPath="admin.table"
            type="select"
          />
        ),
        cell: ({ getValue, row }) => {
          return getCell(
            row.original.datadogApiCredentials,
            getValue(),
            simpleValidator,
            simpleErrorValidator,
          );
        },
      },
    ),
    columnsHelper.accessor(
      c => getEmoji(c.s3_buckets, bucketValidator, bucketErrorValidator),
      {
        id: 's3Buckets',
        header: ({ column }) => (
          <TableHeader
            column={column}
            headersTranslationPath="admin.table"
            type="select"
          />
        ),
        cell: ({ getValue, row }) => {
          return getCell(
            row.original.s3_buckets,
            getValue(),
            bucketValidator,
            bucketErrorValidator,
          );
        },
      },
    ),
    columnsHelper.accessor(
      c => getK8sEmoji(c.k8sClusters, c.unconnected_k8sClusters),
      {
        id: 'k8sClusters',
        header: ({ column }) => (
          <TableHeader
            column={column}
            headersTranslationPath="admin.table"
            type="select"
          />
        ),
        cell: ({ getValue, row }) => {
          return getK8sCell(
            row.original.k8sClusters,
            row.original.unconnected_k8sClusters,
            getValue(),
          );
        },
      },
    ),
    columnsHelper.accessor(
      c => getEmoji(c.grafana, grafanaValidator, grafanaErrorValidator),
      {
        id: 'grafana',
        header: ({ column }) => (
          <TableHeader
            column={column}
            headersTranslationPath="admin.table"
            type="select"
          />
        ),
        cell: ({ getValue, row }) => {
          return getCell(
            row.original.grafana,
            getValue(),
            grafanaValidator,
            grafanaErrorValidator,
          );
        },
      },
    ),
    columnsHelper.accessor(
      c => getEmoji(c.gcpCredentials, gcpValidator, gcpErrorValidator),
      {
        id: 'gcpCredentials',
        header: ({ column }) => (
          <TableHeader
            column={column}
            headersTranslationPath="admin.table"
            type="select"
          />
        ),
        cell: ({ getValue, row }) => {
          return getCell(
            row.original.gcpCredentials,
            getValue(),
            gcpValidator,
            gcpErrorValidator,
          );
        },
      },
    ),
    columnsHelper.accessor(() => 'N/A', {
      id: 'slack',
      header: ({ column }) => (
        <TableHeader
          column={column}
          headersTranslationPath="admin.table"
          type="select"
        />
      ),
      cell: ({ row }) => <SlackStatusCell projectId={row.original.id} />,
    }),
    columnsHelper.accessor('id', {
      header: () => <FormattedMessage id="admin.table.id.header" />,
      cell: ({ getValue }) => {
        if (defaultProjectId === undefined) {
          return null;
        }

        if (getValue() === defaultProjectId) {
          return <p>Current Project</p>;
        }

        return (
          <Button onClick={() => impersonate(getValue())} variant="outline">
            Impersonate
          </Button>
        );
      },
    }),
  ] as ColumnDef<ProjectIntegrations>[];
